/**
 * ADO #291 — Pointer-aware picker entry expansion.
 *
 * Pure helper that, given a list of resources, returns a flat list with
 * synthetic `*<ptr>` (dereference) and `&<scalar>` (address-of) entries
 * inlined right after their base. Used by VariablePicker (Assignment /
 * Decision / Loop / Output template) and SubflowNodeEditor (subflow call
 * arguments) so students never type `*` or `&` by hand.
 *
 * Lives in its own file so unit tests can import it without dragging in
 * Radix UI dependencies via VariablePicker.
 */

const _POINTER_TYPES = new Set(['pointer']);
// ADO #409 — enum_instance is address-of-able (`&today` → enum*), so it's a
// valid scalar source for the synthetic `&name` picker entry.
const _SCALAR_RESOURCE_TYPES = new Set(['variable', 'constant', 'struct_instance', 'union_instance', 'enum_instance']);

const isPointerResource = (r) => {
  if (!r) return false;
  const rt = (r.resourceType || r.type || '').toLowerCase();
  if (_POINTER_TYPES.has(rt)) return true;
  const dt = (r.dataType || '').trim();
  // dataType ends in '*' (e.g. 'int*', 'float *', 'struct Point*') — treat
  // as pointer regardless of the resourceType label.
  return /\*\s*$/.test(dt);
};

const eligibleForAddressOf = (r) => {
  if (!r) return false;
  if (isPointerResource(r)) return false;          // would yield a double pointer
  const rt = (r.resourceType || r.type || '').toLowerCase();
  if (rt.includes('array')) return false;          // array name already decays to pointer
  if (rt === 'formula') return false;               // formulas aren't lvalues
  return _SCALAR_RESOURCE_TYPES.has(rt);
};

const strippedPointerType = (dt) =>
  (dt || '').replace(/\s*\*\s*$/, '').trim() || 'int';

// ADO #354 — pointer depth of a resource. `int**`/`struct_node**` → 2; a
// `resourceType: 'pointer'` with no stars in dataType is one level over its
// pointsToType. Used to gate `&ptr`: we only offer address-of on a SINGLE
// pointer (→ double), never on an already-double pointer (→ a `***` that no
// teaching example needs and that just clutters the picker).
export const pointerDepth = (r) => {
  if (!r) return 0;
  const dt = (r.dataType || '').trim();
  const dtStars = (dt.match(/\*/g) || []).length;
  if (dtStars > 0) return dtStars;
  const rt = (r.resourceType || r.type || '').toLowerCase();
  if (rt === 'pointer') return 1 + ((r.pointsToType || '').match(/\*/g) || []).length;
  return 0;
};

// ADO #354 — canonical C type string for comparison: unify `struct_`/`struct `
// and collapse whitespace around stars so `struct_node**`, `struct node **`
// and `struct node**` all compare equal.
export const normalizeCType = (t) => {
  const s = String(t || '').trim().replace(/struct[_\s]+/i, 'struct ');
  const stars = (s.match(/\*/g) || []).length;
  const base = s.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
  return base + '*'.repeat(stars);
};

// True when a candidate's result type satisfies the slot's expected type. With
// no expected type, everything matches (callers then skip the Suggested split).
// `int`/`integer` and `bool`/`boolean` are treated as equal; otherwise exact
// (after normalization) so a `struct node*` value isn't suggested for a
// `struct node**` parameter.
export const cTypeMatches = (candidateType, expectedType) => {
  if (!expectedType) return true;
  const a = normalizeCType(candidateType);
  const b = normalizeCType(expectedType);
  if (a === b) return true;
  const alias = (t) => t.replace(/^integer$/, 'int').replace(/^boolean$/, 'bool');
  return alias(a) === alias(b);
};

export function expandPointerEntries(variables) {
  const out = [];
  for (const v of (variables || [])) {
    out.push(v);
    if (isPointerResource(v)) {
      out.push({
        ...v,
        id: `${v.id}__deref`,
        name: `*${v.name}`,
        displayName: v.displayName ? `*${v.displayName}` : `*${v.name}`,
        dataType: strippedPointerType(v.dataType),
        // Reset traits that would let the picker drill in / treat as
        // an array — *a is just a scalar value of the pointee type.
        resourceType: 'variable',
        type: 'variable',
        structMembers: undefined,
        members: undefined,
        structTypeRef: undefined,
        _synthetic: 'deref',
        _baseId: v.id,
        _baseName: v.name,
      });
      // ADO #354 — &ptr (address-of a pointer → double pointer). Needed for
      // pass-by-reference calls like `push(&head, ...)` where the callee takes
      // `struct Node**`. Only offered on a SINGLE pointer: address-of an
      // already-double pointer yields a `***` that clutters the list and no
      // teaching example needs.
      if (pointerDepth(v) === 1) {
      const baseDt = (v.dataType || '').trim();
      out.push({
        ...v,
        id: `${v.id}__addr`,
        name: `&${v.name}`,
        displayName: v.displayName ? `&${v.displayName}` : `&${v.name}`,
        dataType: baseDt ? `${baseDt}*` : `${v.pointsToType || 'int'}**`,
        resourceType: 'variable',
        type: 'variable',
        structMembers: undefined,
        members: undefined,
        structTypeRef: undefined,
        _synthetic: 'addr',
        _baseId: v.id,
        _baseName: v.name,
      });
      }
    } else if (eligibleForAddressOf(v)) {
      const baseDt = (v.dataType || '').trim();
      out.push({
        ...v,
        id: `${v.id}__addr`,
        name: `&${v.name}`,
        displayName: v.displayName ? `&${v.displayName}` : `&${v.name}`,
        dataType: baseDt ? `${baseDt}*` : 'int*',
        resourceType: 'variable',
        type: 'variable',
        structMembers: undefined,
        members: undefined,
        structTypeRef: undefined,
        _synthetic: 'addr',
        _baseId: v.id,
        _baseName: v.name,
      });
    }
  }
  return out;
}

export default { expandPointerEntries };
